import * as Tone from 'tone';

type FrequencyRangesType = {
    bass: [number, number];
    lowMid: [number, number];
    mid: [number, number];
    highMid: [number, number];
    treble: [number, number];
}
export class AudioEnergy extends Tone.Analyser {
    private _updated: boolean;
    public frequencyRanges: FrequencyRangesType;

    constructor(sampleCount = 2048) {
        super('fft', sampleCount);
        this._updated = false;

        this.frequencyRanges = {
            bass: [20, 140],
            lowMid: [140, 400],
            mid: [400, 2600],
            highMid: [2600, 5200],
            treble: [5200, 14000],
        };
    }

    _map(n: number, start1: number, stop1: number, start2: number, stop2: number) {
        return ((n - start1) / (stop1 - start1)) * (stop2 - start2) + start2;
    }

    _log(n: number, logBase = 10) {
        return Math.log(n) / Math.log(logBase);
    }

    _lerp(start: number, stop: number, amt: number) {
        return amt * (stop - start) + start;
    }

    update() {
        this._updated = true;
        this.getValue();
    }

    getEnergyAtHz(hz: number) {
        const nyquist = this.context.sampleRate / 2;
        const frequencyBinCount = this.size;
        return Math.max(
            0,
            Math.min(
                frequencyBinCount - 1,
                Math.floor((hz / nyquist) * frequencyBinCount),
            ),
        );
    }

    getEnergyBins(logarithmic = false, n = this.size, minFreq: number, maxFreq: number) {
        if (!this._updated) {
            throw new Error('getEnergy() error: You must call energy.update() first');
        }

        const minFrequency = minFreq || 0;
        const maxFrequency = maxFreq || this.context.sampleRate / 2;
        const logBase = 10;
        const minFrequencyLog = this._log(Math.max(1, minFrequency), logBase);
        const maxFrequencyLog = this._log(Math.max(1, maxFrequency), logBase);

        const bands = [];
        for (let i = 0; i < n; i++) {
            const minT = i / n;
            const maxT = minT + 1 / n;
            let minHz; let
                maxHz;
            if (logarithmic) {
                minHz = logBase ** this._lerp(minFrequencyLog, maxFrequencyLog, minT);
                maxHz = logBase ** this._lerp(minFrequencyLog, maxFrequencyLog, maxT);
            } else {
                minHz = this._lerp(minFrequency, maxFrequency, minT);
                maxHz = this._lerp(minFrequency, maxFrequency, maxT);
            }
            const energy = this.getEnergy(minHz, maxHz);
            bands.push(energy);
        }
        return bands;
    }

    getEnergy(minHz: number | keyof FrequencyRangesType, maxHz?: number) {
        if (!this._updated) {
            throw new Error('getEnergy() error: You must call energy.update() first');
        }
        // eslint-disable-next-line prefer-rest-params
        const args = Array.from(arguments);
        const frequencyRanges: any = this.frequencyRanges;
        let low; let
            high;
        if (args.length === 1 && typeof args[0] === 'string') {
            if (args[0] in frequencyRanges) {
                const range = frequencyRanges[args[0]];
                low = range[0];
                high = range[1];
            } else {
                throw new Error(
                    `getEnergy() error: No range called ${
                        args[0]
                    } - possible ranges are: ${Object.keys(frequencyRanges).join(
                        ', ',
                    )}`,
                );
            }
        } else if (args.length === 2) {
            low = args[0];
            high = args[1];
        }

        let total = 0;
        let numFrequencies = 0;
        const buffers: number[] = this.getValue() as unknown as number[];
        const lowIndex = this.getEnergyAtHz(low);
        const highIndex = Math.min(this.getEnergyAtHz(high), buffers.length - 1);
        for (let index = lowIndex; index <= highIndex; index++) {
            const num = buffers[index];
            total += num;
            numFrequencies++;
        }
        // console.log(total, numFrequencies);
        // divide by total number of frequencies
        const level = (100 + total / numFrequencies) / 100;
        return level;
    }
}

const beatThreshold = 0.01;
const beatHoldFrames = 50;
const beatPeakRate = 1.15;
const beatIgnoreLevel = 0.4;
const beatDecayRate = 0.6; // how fast does beat cutoff decay?

export const useDetectBeat = () => {
    let framesSinceLastBeat = 0;
    let beatCutoff = 0;

    function detectBeat(level: number) {
        if (beatIgnoreLevel > level) {
            return false;
        }
        if (level > beatCutoff && level > beatThreshold) {
        // onBeat();
            if (process.env.IS_DEV) {
                console.log('beat');
            }
            beatCutoff = level * beatPeakRate;
            framesSinceLastBeat = 0;
            return true;
        } if (framesSinceLastBeat <= beatHoldFrames) {
            framesSinceLastBeat++;
        } else {
            beatCutoff *= beatDecayRate;
            beatCutoff = Math.max(beatCutoff, beatThreshold);
        }
        return false;
    }

    return { detectBeat };
};
